// One-time language prompt — iter76 (Spanish-first launch).
//
// Behaviour:
//   • Default app language is now ES for everybody.
//   • If the browser is set to English AND the user is currently on ES, we
//     surface a small gold pill asking "¿Cambiar a Inglés?" / "Switch to
//     English?" Accept → app flips to EN. Decline → never shown again.
//   • Mirror flow if the user has somehow ended up on EN but the browser is
//     Spanish — we surface the original "¿Cambiar a Español?" pill.
//
// Storage:
//   habano.lang.prompt.dismissed — "1" once the pill has been acted on.
import { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import { useI18n } from "../lib/i18n";

const LS_KEY = "habano.lang.prompt.dismissed";

function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const candidates = [];
  if (navigator.languages && navigator.languages.length) candidates.push(...navigator.languages);
  if (navigator.language) candidates.push(navigator.language);
  for (const c of candidates) {
    const code = (c || "").toLowerCase().slice(0, 2);
    if (code === "es") return "es";
    if (code === "en") return "en";
  }
  return "en";
}

export default function LanguagePrompt() {
  const { lang, setLang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [target, setTarget] = useState(null); // "en" | "es"

  useEffect(() => {
    try {
      if (localStorage.getItem(LS_KEY) === "1") return;
      const browser = detectBrowserLang();
      // If the user's current locale already matches the browser, nothing to ask.
      if (browser === lang) {
        localStorage.setItem(LS_KEY, "1");
        return;
      }
      setTarget(browser);
      setVisible(true);
    } catch {
      /* localStorage unavailable */
    }
  }, [lang]);

  if (!visible || !target) return null;

  const persistDismissal = () => {
    try { localStorage.setItem(LS_KEY, "1"); } catch { /* no-op */ }
  };

  const accept = () => {
    setLang(target);
    persistDismissal();
    setVisible(false);
  };

  const decline = () => {
    persistDismissal();
    setVisible(false);
  };

  // iter128 — Bilingual UX rule: the prompt text is rendered in the TARGET
  // language so a Spanish-speaking traveler sees a Spanish prompt and an
  // English-speaking traveler sees an English prompt. (Previously both
  // variants spoke Spanish, which broke the rule for EN→ES nudges.)
  const promptText = target === "en" ? "Switch to English?" : "¿Cambiar a Español?";
  const acceptLabel = target === "en" ? "Switch to English" : "Cambiar a Español";

  return (
    <div
      data-testid="lang-prompt-pill"
      role="status"
      aria-live="polite"
      style={{
        marginTop: 10,
        marginLeft: 16,
        marginRight: 16,
        padding: "8px 12px",
        background: "rgba(232,184,75,0.08)",
        border: "1px solid rgba(232,184,75,0.45)",
        borderRadius: 999,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 12,
        color: "#E8B84B",
      }}
    >
      <span style={{ flex: 1, lineHeight: 1.3 }}>{promptText}</span>
      <button
        type="button"
        data-testid="lang-prompt-accept"
        aria-label={acceptLabel}
        onClick={accept}
        style={{
          minWidth: 48, minHeight: 48,
          display: "grid", placeItems: "center",
          background: "#E8B84B", color: "#0B0B0B",
          border: "none", borderRadius: 999,
          margin: "-8px 0 -8px -4px", padding: 0,
        }}
      >
        <Check className="w-4 h-4" strokeWidth={2} />
      </button>
      <button
        type="button"
        data-testid="lang-prompt-dismiss"
        aria-label="Dismiss"
        onClick={decline}
        style={{
          minWidth: 48, minHeight: 48,
          display: "grid", placeItems: "center",
          background: "transparent", color: "#E8B84B",
          border: "none", padding: 0,
          margin: "-8px -8px -8px 0",
        }}
      >
        <X className="w-4 h-4" strokeWidth={1.5} />
      </button>
    </div>
  );
}
